/**
 * 权限常量 - 自动从 PERMISSIONS_CONFIG 生成
 *
 * ⚠️ 这是从 PERMISSIONS_CONFIG 自动派生的，不要直接修改
 * ⚠️ 请修改 resources/*.config.ts 中的 permissions 字段
 *
 * @example
 * ```typescript
 * import { P } from "@crm/access/authorization/config/constants";
 *
 * // 使用常量
 * access([P.STUDENT_READ_ALL])
 * hasPermission(permissions, P.MESSAGE_WRITE_ASSIGNED)
 * ```
 */

import { PERMISSIONS_CONFIG } from "./resources";

// ============================================
// 工具函数
// ============================================

/**
 * 将资源类型和权限键转换为常量名
 * @example
 * toConstantName("class-session", "read:all") → "CLASS_SESSION_READ_ALL"
 */
function toConstantName(resourceType: string, permissionKey: string): string {
  const resource = resourceType.toUpperCase().replace(/-/g, "_");
  const permission = permissionKey.toUpperCase().replace(/:/g, "_");
  return `${resource}_${permission}`;
}

/**
 * 将资源类型和权限键转换为权限字符串
 * @example
 * toPermissionString("class-session", "read:all") → "class-session:read:all"
 */
function toPermissionString(resourceType: string, permissionKey: string): string {
  return `${resourceType}:${permissionKey}`;
}

// ============================================
// 自动生成权限常量
// ============================================

/**
 * 从 PERMISSIONS_CONFIG 自动生成权限常量映射
 */
function generatePermissionConstants() {
  const constants: Record<string, string> = {};

  for (const [resourceType, config] of Object.entries(PERMISSIONS_CONFIG)) {
    for (const permissionKey of Object.keys(config.permissions)) {
      const constantName = toConstantName(resourceType, permissionKey);
      const permissionString = toPermissionString(resourceType, permissionKey);
      constants[constantName] = permissionString;
    }
  }

  return constants as Record<string, string>;
}

// ============================================
// 类型定义
// ============================================

/**
 * 从 PERMISSIONS_CONFIG 推导权限常量类型
 *
 * 生成形如 { STUDENT_READ_ALL: "student:read:all", ... } 的类型
 */
type GeneratedPermissionConstants = {
  [Resource in keyof typeof PERMISSIONS_CONFIG as Uppercase<
    Resource extends string ? Replace<Resource, "-", "_"> : never
  > extends infer R
    ? R extends string
      ? keyof (typeof PERMISSIONS_CONFIG)[Resource]["permissions"] extends infer Perm
        ? Perm extends string
          ? `${R}_${Uppercase<Replace<Perm, ":", "_">>}`
          : never
        : never
      : never
    : never]: `${Resource & string}:${keyof (typeof PERMISSIONS_CONFIG)[Resource]["permissions"] &
    string}`;
};

/**
 * 字符串替换类型辅助
 */
type Replace<
  S extends string,
  From extends string,
  To extends string,
> = S extends `${infer L}${From}${infer R}` ? `${L}${To}${Replace<R, From, To>}` : S;

// ============================================
// 导出
// ============================================

/**
 * 权限常量对象
 *
 * 从 PERMISSIONS_CONFIG 自动生成，包含所有权限的类型安全常量
 *
 * @example
 * ```typescript
 * import { P } from "@crm/access/authorization/config";
 *
 * // 类型安全的权限引用
 * access([P.STUDENT_READ_ALL])
 * hasPermission(permissions, P.CLASS_SESSION_WRITE_ASSIGNED)
 * ```
 */
export const P = generatePermissionConstants() as GeneratedPermissionConstants;

/**
 * 权限常量类型
 */
export type PermissionConstant = (typeof P)[keyof typeof P];
